import React, { useEffect, useRef, useState } from 'react'
import ZipProgressDialog from '../components/ZipProgressDialog'
import { useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { startZipDownloadFromWorker, ZipDownloadCancelledError, type ZipBuildProgress } from '../lib/clientZip'

const ENTRY_CATEGORY_TABS = ['Mono', 'Colour', 'Themed'] as const
const MOBILE_WIDTH_MEDIA_QUERY = '(max-width: 760px)'

function defaultEntriesColumns() {
  if (typeof window !== 'undefined' && window.matchMedia(MOBILE_WIDTH_MEDIA_QUERY).matches) return 2
  return 3
}

type Entry = {
  id: number
  competitionNumber: string
  className: 'Novice'|'Intermediate'|'Advanced'
  categoryLabel: 'Mono'|'Colour'|'Themed'
  title: string
  zipPath: string
  previewUrl: string
  myScore?: number | null
  myComment?: string | null
}

type Resp = {
  competition: { title:string, slug:string, status:string }
  judge: { name: string }
  locked: boolean
  zipUrl?: string
  entries: Entry[]
}
type ZipDialogState = {
  title: string
  progress: ZipBuildProgress
  cancel: () => void
}

export default function Judge() {
  const { slug, token } = useParams()
  const [data, setData] = useState<Resp | null>(null)
  const [draftScores, setDraftScores] = useState<Record<number, string>>({})
  const [draftComments, setDraftComments] = useState<Record<number, string>>({})
  const [err, setErr] = useState('')
  const [ok, setOk] = useState('')
  const [columns, setColumns] = useState(defaultEntriesColumns)
  const [activeClass, setActiveClass] = useState('')
  const [activeCategory, setActiveCategory] = useState('')
  const [toast, setToast] = useState<{ message: string, type: 'success'|'error' } | null>(null)
  const [savingByEntry, setSavingByEntry] = useState<Record<number, boolean>>({})
  const [savedByEntry, setSavedByEntry] = useState<Record<number, boolean>>({})
  const [zipDialog, setZipDialog] = useState<ZipDialogState | null>(null)
  const savedResetTimers = useRef<Record<number, number>>({})
  const activeZipCancelRef = useRef<(() => void) | null>(null)

  function showToast(message: string, type: 'success'|'error' = 'error') {
    setToast({ message, type })
    window.setTimeout(() => setToast(null), 3000)
  }

  async function load() {
    setErr(''); setOk('')
    try {
      setColumns(defaultEntriesColumns())
      setData(await api<Resp>(`/api/judges/${slug}/${token}`))
    }
    catch (e:any) { setErr(e.message); showToast(e.message || 'Could not load judging page.', 'error') }
  }
  useEffect(()=>{ load() }, [slug, token])
  useEffect(() => {
    return () => {
      Object.values(savedResetTimers.current).forEach((timerId) => window.clearTimeout(timerId))
      activeZipCancelRef.current?.()
    }
  }, [])
  useEffect(() => {
    if (!data) return
    const nextScores: Record<number, string> = {}
    const nextComments: Record<number, string> = {}
    for (const e of data.entries) {
      nextScores[e.id] = e.myScore == null ? '' : String(e.myScore)
      nextComments[e.id] = e.myComment ?? ''
    }
    setDraftScores(nextScores)
    setDraftComments(nextComments)
  }, [data])

  const classOrder: Record<Entry['className'], number> = {
    Novice: 0,
    Intermediate: 1,
    Advanced: 2
  }
  const entryClasses = data
    ? Array.from(new Set(data.entries.map(entry => entry.className))).sort(
        (a, b) => classOrder[a] - classOrder[b]
      )
    : []
  const activeClassValue = entryClasses.includes(activeClass as Entry['className']) ? activeClass : (entryClasses[0] || '')
  const availableCategories = activeClassValue
    ? ENTRY_CATEGORY_TABS.filter(category =>
        data?.entries.some(entry => entry.className === activeClassValue && entry.categoryLabel === category)
      )
    : []
  const activeCategoryValue = availableCategories.includes(activeCategory as typeof ENTRY_CATEGORY_TABS[number])
    ? activeCategory
    : (availableCategories[0] || '')
  const visibleEntries = data
    ? data.entries.filter(entry => entry.className === activeClassValue && entry.categoryLabel === activeCategoryValue)
    : []

  function validateEntry(entryId: number) {
    const scoreRaw = (draftScores[entryId] ?? '').trim()
    if (scoreRaw === '') return 'Score is required.'
    const scoreNum = Number(scoreRaw)
    if (!Number.isFinite(scoreNum) || scoreNum < 1 || scoreNum > 50 || !Number.isInteger(scoreNum)) {
      return 'Score must be a whole number between 1 and 50.'
    }
    const comment = draftComments[entryId] ?? ''
    if (comment.length > 2000) return 'Comment must be 2000 characters or fewer.'
    return ''
  }

  async function save(entryId: number) {
    setErr(''); setOk('')
    const validationMessage = validateEntry(entryId)
    if (validationMessage) {
      setErr(validationMessage)
      showToast(validationMessage, 'error')
      return
    }
    const scoreRaw = (draftScores[entryId] ?? '').trim()
    const scoreNum = Number(scoreRaw)
    const comment = draftComments[entryId] ?? ''
    try {
      setSavingByEntry(prev => ({ ...prev, [entryId]: true }))
      await Promise.all([
        api<{ok:true}>(`/api/judges/${slug}/${token}/score`, {
          method:'POST',
          headers:{'content-type':'application/json'},
          body: JSON.stringify({ entryId, score: scoreNum, comment })
        }),
        new Promise(resolve => setTimeout(resolve, 450))
      ])
      setOk('Saved.')
      setSavedByEntry(prev => ({ ...prev, [entryId]: true }))
      if (savedResetTimers.current[entryId]) window.clearTimeout(savedResetTimers.current[entryId])
      savedResetTimers.current[entryId] = window.setTimeout(() => {
        setSavedByEntry(prev => ({ ...prev, [entryId]: false }))
        delete savedResetTimers.current[entryId]
      }, 1500)
    } catch (e:any) {
      setErr(e.message)
      showToast(e.message || 'Could not save score.', 'error')
    } finally {
      setSavingByEntry(prev => ({ ...prev, [entryId]: false }))
    }
  }

  async function downloadFullSet() {
    if (!data) return
    setErr('')
    try {
      if (data.entries.length === 0) throw new Error('No uploaded entries are available for this competition.')
      const task = startZipDownloadFromWorker(
        data.entries.map((entry) => ({ name: entry.zipPath, url: entry.previewUrl })),
        `${data.competition.slug}.zip`,
        (progress) => setZipDialog((prev) => prev ? { ...prev, progress } : prev)
      )
      activeZipCancelRef.current = task.cancel
      setZipDialog({
        title: data.competition.title,
        progress: {
          phase: 'downloading',
          completed: 0,
          total: data.entries.length
        },
        cancel: task.cancel
      })
      await task.done
    } catch (e:any) {
      if (!(e instanceof ZipDownloadCancelledError)) setErr(e.message)
    } finally {
      activeZipCancelRef.current = null
      setZipDialog(null)
    }
  }

  return (
    <div className="card">
      {toast && <div className={`toast ${toast.type}`}>{toast.message}</div>}
      {err && <p className='error-text'>{err}</p>}
      {ok && <p><small className="muted">{ok}</small></p>}
      {!data ? (err ? null : <p><small className="muted">Loading…</small></p>) : (
        <>
          <h2 style={{marginTop:0}}>{data.competition.title} — Judging</h2>
          <div className="row" style={{justifyContent:'space-between'}}>
            <div className="row">
              <span className="badge">{data.judge.name}</span>
              <span className="badge">{data.locked ? 'scoring closed' : 'scoring open'}</span>
            </div>
            <div className="row">
              {data.zipUrl && (
                <button
                  type="button"
                  className="text-link-button"
                  onClick={() => { void downloadFullSet() }}
                  disabled={Boolean(zipDialog)}
                >
                  {zipDialog ? 'Preparing zip...' : 'Download full set (zip)'}
                </button>
              )}
            </div>
          </div>

          {data.locked && (
            <div className="error-text" style={{marginTop:12}}>
              <b>Judging is not open yet.</b> You can view entries now, but scoring fields will appear only when judging opens.
            </div>
          )}

          <hr />

          <div className="entry-filter-controls">
            <div>
              <label>Class</label>
              <select
                value={activeClassValue}
                onChange={(e) => setActiveClass(e.target.value)}
                style={{minWidth:200, width:'50%'}}
              >
                {entryClasses.map(className => (
                  <option key={className} value={className}>{className}</option>
                ))}
              </select>
            </div>
            <div>
              <label>Images across</label>
              <select
                value={columns}
                onChange={(e) => setColumns(Number(e.target.value))}
                style={{minWidth:80, width:'100%'}}
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
              </select>
            </div>
          </div>
          <div className="entry-filter-category-row">
            <div className="entry-filter-category-tabs">
              {ENTRY_CATEGORY_TABS.map(category => {
                const isAvailable = availableCategories.includes(category)
                return (
                  <button
                    key={category}
                    type="button"
                    className={category === activeCategoryValue ? 'primary' : undefined}
                    onClick={() => setActiveCategory(category)}
                    disabled={!isAvailable}
                  >
                    {category}
                  </button>
                )
              })}
            </div>
          </div>

          <div style={{display:'grid', gap: 12, gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, marginTop: 12}}>
            {visibleEntries.map(e => (
              <div key={e.id} className="card" style={{background:'#0b1220'}}>
                <div>
                  <div style={{fontWeight:700}}>{e.competitionNumber}</div>
                  <div><small className="muted">{e.title}</small></div>
                </div>

                <div style={{marginTop:10}}>
                  <a href={e.previewUrl} target="_blank" rel="noreferrer">
                    <img src={e.previewUrl} alt={e.title} style={{width:'100%', borderRadius:0, border:'1px solid rgba(255,255,255,0.45)'}} loading="lazy" />
                  </a>
                </div>

                {!data.locked && (
                  <div style={{display:'grid', gap:12, marginTop: 12}}>
                    <div className="judge-score-field">
                      <label>Score (1–50)</label>
                      <input
                        type="number"
                        min={1}
                        max={50}
                        className="judge-score-input"
                        value={draftScores[e.id] ?? ''}
                        onChange={(ev) => {
                          const nextValue = ev.currentTarget.value
                          setDraftScores(prev => ({ ...prev, [e.id]: nextValue }))
                        }}
                        placeholder="1-50"
                      />
                    </div>
                    <div>
                      <label>Comment (optional)</label>
                      <textarea
                        value={draftComments[e.id] ?? ''}
                        maxLength={2000}
                        onChange={(ev) => {
                          const nextValue = ev.currentTarget.value
                          setDraftComments(prev => ({ ...prev, [e.id]: nextValue }))
                        }}
                      />
                    </div>
                    {validateEntry(e.id) && <small className="muted">{validateEntry(e.id)}</small>}
                    <button
                      className="primary"
                      onClick={() => save(e.id)}
                      disabled={Boolean(validateEntry(e.id)) || Boolean(savingByEntry[e.id])}
                    >
                      {savingByEntry[e.id] ? (
                        <span className="row" style={{gap:8}}>
                          <span className="btn-spinner" />
                          <span>Saving...</span>
                        </span>
                      ) : savedByEntry[e.id] ? 'Saved' : 'Save score'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
      {zipDialog && (
        <ZipProgressDialog
          title={zipDialog.title}
          progress={zipDialog.progress}
          onCancel={zipDialog.cancel}
        />
      )}
    </div>
  )
}
